/**
 * Pollen Dots — Unified Profile page.
 *
 * Replaces the old Pulse / Contributions / Team-leaderboard tab sprawl.
 * Each user sees one Profile tab with:
 *   • Their own Pulse (cooperative metrics — TAT, conversions, handoffs)
 *   • Their Contributions (tours, vendors, locations, customers, activity)
 * Super Admins and supervisors additionally see a "Team" section listing
 * the teammates they can support — clicking opens that teammate's profile
 * (same shape, viewed read-only).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Navigate, useParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import PollenDotsNav from '@/components/PollenDotsNav';
import { API, useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import {
  User, Compass, Building2, MapPin, Users, IndianRupee, Activity, Loader2,
  ArrowRight, Clock, Search, ChevronDown, ChevronUp, Pin,
  Inbox, Briefcase, HeartHandshake, ArrowLeft, ShieldCheck, CheckCircle2,
  HandHelping, AlertCircle, ClipboardList, Plus, Hotel as HotelIcon, Car, UserCheck, Globe, Megaphone, Trash2, ExternalLink,
} from 'lucide-react';
import { usePollenCapabilities } from '@/hooks/usePollenCapabilities';
import { formatDate } from '@/lib/dateUtils';
import { useOperatorName } from '@/hooks/useOperatorName';
import DataGapsPulseCard from '@/components/DataGapsPulseCard';
import TaskLeaderboardChip from '@/components/TaskLeaderboardChip';

const POLLEN_USER_ROLES = ['Pollen Dots User', 'Super Admin', 'Admin', 'Operator', 'Intern'];

const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const PollenDotsMyProfile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const caps = usePollenCapabilities();
  const params = useParams();
  // When :userId is present we're viewing a teammate's profile (Super
  // Admin / Admin / supervisor only). Otherwise it's the caller's own
  // profile. We also accept ?email-based lookup for the Team-tab
  // Performance button (which only knows the team-member email).
  const viewingUserId = params.userId || null;
  const viewingEmailParam = params.email ? decodeURIComponent(params.email) : null;
  const isOwn = !viewingUserId && !viewingEmailParam;
  const POLLEN_API = `${API}/pollen`;
  const [period, setPeriod] = useState('month');
  const [contrib, setContrib] = useState(null);
  const [pulse, setPulse] = useState(null);
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPins = async () => {
    if (!isOwn) { setPins([]); return; }
    try {
      const res = await fetch(`${POLLEN_API}/me/pins`, { credentials: 'include', headers: authHeaders() });
      if (res.ok) setPins(await res.json());
    } catch { /* noop */ }
  };

  const unpin = async (p) => {
    try {
      await fetch(`${POLLEN_API}/me/pins/${p.entity_type}/${p.entity_id}`, { method: 'DELETE', credentials: 'include', headers: authHeaders() });
      setPins(prev => prev.filter(x => !(x.entity_type === p.entity_type && x.entity_id === p.entity_id)));
    } catch { /* noop */ }
  };

  const pinLinkFor = (p) => {
    if (p.entity_type === 'tour') return `/pollen-dots/tour/${p.entity_id}`;
    if (p.entity_type === 'vendor') return `/pollen-dots/vendor/${p.entity_id}`;
    if (p.entity_type === 'customer') return `/pollen-dots/customer/${p.entity_id}`;
    if (p.entity_type === 'team') return `/pollen-dots/team/${p.entity_id}`;
    return '#';
  };

  useEffect(() => { fetchPins(); /* eslint-disable-next-line */ }, [isOwn]);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        // Contributions payload — uses `/me/profile` for self, `/team/.../profile`
        // is super-admin-only (and keyed by team_member id, not user_id) so for
        // teammate views we just rely on the Pulse payload + skip the
        // contributions feed when looking at someone else.
        const contribURL = isOwn ? `${POLLEN_API}/me/profile?period=${period}` : null;
        const pulseURL = isOwn
          ? `${POLLEN_API}/performance/me?period=${period}`
          : (viewingUserId
              ? `${POLLEN_API}/performance/by-user/${viewingUserId}?period=${period}`
              : `${POLLEN_API}/performance/by-email?email=${encodeURIComponent(viewingEmailParam)}&period=${period}`);

        const tasks = [
          contribURL ? fetch(contribURL, { credentials: 'include', headers: authHeaders() }).then(r => r.ok ? r.json() : null) : Promise.resolve(null),
          fetch(pulseURL, { credentials: 'include', headers: authHeaders() }).then(r => r.ok ? r.json() : null),
        ];
        const [c, p] = await Promise.all(tasks);
        setContrib(c);
        setPulse(p);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [period, POLLEN_API, isOwn, viewingUserId, viewingEmailParam]);

  // Resolve operator name for the header email — MUST run unconditionally
  // (before any early returns) to honour the rules of hooks.
  const headerEmail = isOwn ? (contrib?.user?.email || user?.email) : (pulse?.email || '');
  const resolvedName = useOperatorName(headerEmail, '');

  if (!POLLEN_USER_ROLES.includes(user?.role)) return <Navigate to="/dashboard" replace />;

  const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  // (headerEmail and resolvedName computed above so hooks run unconditionally)
  const fallbackName = isOwn ? (contrib?.user?.name || user?.name || 'My Profile') : (pulse?.name || 'Profile');
  const headerName = resolvedName || fallbackName;
  const headerRole = isOwn ? (contrib?.user?.role || user?.role) : (pulse?.role || '');

  return (
    <Layout>
      <div className="space-y-4" data-testid={isOwn ? 'my-profile-page' : 'team-profile-page'}>
        <PollenDotsNav />

        {!isOwn && (
          <button onClick={() => navigate('/pollen-dots/my')}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            data-testid="back-to-own-profile">
            <ArrowLeft className="w-3 h-3" />Back to my profile
          </button>
        )}

        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="profile-name">
                {headerName}
                {!isOwn && (
                  <Badge variant="outline" className="ml-1 text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                    <ShieldCheck className="w-3 h-3 mr-1" />Supervisor view
                  </Badge>
                )}
              </h1>
              <div className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
                <span>{headerEmail}</span>
                {headerRole && (<>
                  <span>·</span>
                  <Badge variant="outline">{headerRole}</Badge>
                </>)}
              </div>
            </div>
          </div>
          <Tabs value={period} onValueChange={setPeriod} className="ml-auto">
            <TabsList className="h-9">
              <TabsTrigger value="week" className="text-xs px-3" data-testid="period-week">Week</TabsTrigger>
              <TabsTrigger value="month" className="text-xs px-3" data-testid="period-month">Month</TabsTrigger>
              <TabsTrigger value="quarter" className="text-xs px-3" data-testid="period-quarter">Quarter</TabsTrigger>
              <TabsTrigger value="year" className="text-xs px-3" data-testid="period-year">Year</TabsTrigger>
              <TabsTrigger value="lifetime" className="text-xs px-3" data-testid="period-lifetime">Lifetime</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-[40vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : (
          <>
            {/* --- Task leaderboard chip (rolling 7d throughput) ----------- */}
            <TaskLeaderboardChip
              email={isOwn ? (caps.email || user?.email || '') : pulse?.email}
            />

            {/* --- Pulse --------------------------------------------------- */}
            {pulse && <PulseCard m={pulse} />}

            {/* --- Listing assignments + Assign Task ---------------------- */}
            <ListingsCard
              listings={pulse?.listings || {}}
              isOwn={isOwn}
              canAssign={!!(isOwn && (caps.is_super_admin || caps.is_supervisor))}
              viewingEmail={isOwn ? (caps.email || user?.email || '') : pulse?.email}
              caps={caps}
              onChange={() => {
                // Silent refresh so the open dialog (with its share panel)
                // doesn't get unmounted by the loading skeleton.
                const url = isOwn
                  ? `${POLLEN_API}/performance/me?period=${period}`
                  : (viewingUserId
                      ? `${POLLEN_API}/performance/by-user/${viewingUserId}?period=${period}`
                      : `${POLLEN_API}/performance/by-email?email=${encodeURIComponent(viewingEmailParam)}&period=${period}`);
                fetch(url, { credentials: 'include', headers: authHeaders() })
                  .then(r => r.ok ? r.json() : null)
                  .then(p => { if (p) setPulse(p); });
              }}
            />

            {/* --- Data-gap tasks (one-tap inline fixers) ------------------- */}
            <DataGapsPulseCard
              counts={pulse?.data_gaps || { open: 0, done: 0 }}
              assigneeEmail={isOwn ? (caps.email || user?.email || '') : pulse?.email}
              onChange={() => {
                const url = isOwn
                  ? `${POLLEN_API}/performance/me?period=${period}`
                  : (viewingUserId
                      ? `${POLLEN_API}/performance/by-user/${viewingUserId}?period=${period}`
                      : `${POLLEN_API}/performance/by-email?email=${encodeURIComponent(viewingEmailParam)}&period=${period}`);
                fetch(url, { credentials: 'include', headers: authHeaders() })
                  .then(r => r.ok ? r.json() : null)
                  .then(p => { if (p) setPulse(p); });
              }}
            />

            {/* --- Pinned (own profile only) ------------------------------- */}
            {isOwn && pins.length > 0 && (
              <Card>
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Pin className="w-4 h-4 text-primary" />Pinned Items
                    <Badge variant="outline" className="text-[10px]">{pins.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {pins.map(p => (
                      <button
                        key={`${p.entity_type}-${p.entity_id}`}
                        type="button"
                        onClick={() => navigate(pinLinkFor(p))}
                        className="group flex items-center gap-2 px-3 py-1.5 rounded-full border bg-white hover:bg-muted transition-colors"
                        data-testid={`pin-chip-${p.entity_id}`}>
                        <Badge variant="outline" className="text-[10px] h-4 px-1 capitalize">{p.entity_type}</Badge>
                        <span className="text-xs font-medium truncate max-w-[200px]">{p.title}</span>
                        {p.subtitle && <span className="text-[10px] text-muted-foreground truncate max-w-[140px]">{p.subtitle}</span>}
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); unpin(p); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); unpin(p); } }}
                          className="text-muted-foreground hover:text-destructive ml-1"
                          data-testid={`unpin-${p.entity_id}`}
                          aria-label="Unpin">×</span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* --- Contributions (own profile only) ----------------------- */}
            {isOwn && contrib && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                  <SummaryTile icon={Compass} label="Tours" value={contrib.summary.tours} tint="text-orange-600" />
                  <SummaryTile icon={Building2} label="Vendors" value={contrib.summary.vendors} tint="text-blue-600" />
                  <SummaryTile icon={MapPin} label="Locations" value={contrib.summary.locations} tint="text-sky-600" />
                  <SummaryTile icon={Users} label="Customers" value={contrib.summary.customers} tint="text-indigo-600" />
                  <SummaryTile icon={Activity} label="Activity" value={contrib.summary.activity_events} tint="text-rose-600" />
                  {caps.can_view_prices && <SummaryTile icon={IndianRupee} label="Revenue" value={fmtMoney(contrib.summary.revenue)} tint="text-emerald-600" />}
                </div>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Weekly Contributions (last 12 weeks)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-52">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={contrib.trend}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="week_start" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                          <Tooltip />
                          <Area type="monotone" dataKey="tours" stackId="1" stroke="#f97316" fill="#f97316" fillOpacity={0.4} name="Tours" />
                          <Area type="monotone" dataKey="vendors" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.4} name="Vendors" />
                          <Area type="monotone" dataKey="locations" stackId="1" stroke="#0ea5e9" fill="#0ea5e9" fillOpacity={0.4} name="Locations" />
                          <Area type="monotone" dataKey="customers" stackId="1" stroke="#6366f1" fill="#6366f1" fillOpacity={0.4} name="Customers" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <ContribTabs data={contrib} caps={caps} navigate={navigate} />
                <ActivityHistory activity={contrib.activity} />
              </>
            )}

            {/* --- Tasks I've assigned to teammates (creator's inbox) ---- */}
            {isOwn && (caps.is_super_admin || caps.is_supervisor) && (
              <div id="tasks-assigned-card" className="scroll-mt-20">
                <TasksAssignedCard
                  myEmail={caps.email || user?.email || ''}
                  period={period}
                  navigate={navigate}
                  caps={caps}
                  isSuper={!!caps.is_super_admin}
                />
              </div>
            )}

            {/* --- Team section (visible to supervisors / Super Admins) ----
                 We render two sibling cards instead of nesting the
                 SupervisorManager inside TeamSection, so Super Admins can
                 always reach the assignment UI even before they have any
                 teammates set up. */}
            {isOwn && (caps.is_super_admin || caps.is_supervisor) && (
              <TeamSection period={period} navigate={navigate} mySelfId={pulse?.user_id} caps={caps} />
            )}
            {isOwn && caps.is_super_admin && (
              <CollapsibleSection
                icon={ShieldCheck}
                title="Supervisor Assignments"
                summary="Decide which Admins can support which teammates (coaching/support only)."
                badge="Super Admin"
                testid="supervisor-manager-card"
              >
                <SupervisorManager />
              </CollapsibleSection>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

// --- Pulse card — cooperative metrics at a glance ----------------------------
const PulseCard = ({ m }) => {
  const [open, setOpen] = useState(false);
  const s = m.sales || {};
  const o = m.operations || {};
  const types = Object.entries(s.by_task_type || {});
  return (
    <Card data-testid="profile-pulse">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold flex items-center gap-2">
            <Activity className="w-4 h-4 text-amber-700" />My Pulse · {m.period}
          </div>
          <button
            type="button"
            onClick={() => setOpen(v => !v)}
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            data-testid="pulse-toggle"
          >{open ? 'Hide detail' : 'See detail'} {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}</button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Tasks done" value={s.tasks_completed ?? 0} hint={`${s.tasks_open ?? 0} open`} testid="pulse-tasks" />
          <Stat label="Avg task TAT" value={s.avg_task_tat_hours != null ? `${s.avg_task_tat_hours} h` : '—'} testid="pulse-tat" />
          <Stat label="Conversions" value={`${s.converted_to_tour ?? 0}`} hint={`${s.conversion_rate ?? 0}% of ${s.queries_logged ?? 0} queries`} testid="pulse-conv" />
          <Stat label="Tours done" value={o.tours_completed ?? 0} hint={`${o.tours_in_progress ?? 0} in progress`} testid="pulse-tours-done" />
        </div>

        {/* Cooperative handoffs — separate row so they read as "support" not "score" */}
        <div className="grid grid-cols-2 gap-2">
          <Stat
            icon={HeartHandshake}
            label="Handoffs assisted"
            value={s.handoffs_received ?? 0}
            hint="Tasks I took over and finished"
            tint="text-emerald-700"
            testid="pulse-assists-received"
          />
          <Stat
            icon={HeartHandshake}
            label="Handoffs given"
            value={s.handoffs_given ?? 0}
            hint="Tasks teammates finished for me"
            tint="text-sky-700"
            testid="pulse-assists-given"
          />
        </div>

        {open && (
          <>
            <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
              <div className="text-[11px] uppercase tracking-wide text-amber-800 font-semibold flex items-center gap-1.5">
                <Inbox className="w-3.5 h-3.5" /> Sales · Query → Tour
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Stat label="Queries logged" value={s.queries_logged ?? 0} />
                <Stat label="Converted" value={s.converted_to_tour ?? 0} hint={`${s.conversion_rate ?? 0}%`} />
              </div>
              {types.length > 0 && (
                <div className="space-y-1.5 pt-1">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">By task type</div>
                  {types.map(([type, v]) => (
                    <div key={type} className="flex items-center justify-between text-xs py-1">
                      <span className="truncate flex-1 mr-2">{type}</span>
                      <span className="font-mono">{v.count}× · avg {v.avg_tat_hours} h</span>
                    </div>
                  ))}
                </div>
              )}
              {s.lifecycle_tat_hours && Object.values(s.lifecycle_tat_hours).some(x => x?.samples) && (
                <div className="space-y-1.5 pt-1">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Stage TAT (median)</div>
                  {Object.entries(s.lifecycle_tat_hours).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between text-xs py-1">
                      <span className="truncate flex-1 mr-2 inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" />{k}
                      </span>
                      <span className="font-mono">{v.samples ? `${v.median_hours} h (n=${v.samples})` : '—'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
              <div className="text-[11px] uppercase tracking-wide text-emerald-800 font-semibold flex items-center gap-1.5">
                <Briefcase className="w-3.5 h-3.5" /> Operations · Tour → Completed
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Stat label="Created" value={o.tours_created ?? 0} />
                <Stat label="In progress" value={o.tours_in_progress ?? 0} />
                <Stat label="Completed" value={o.tours_completed ?? 0} />
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const Stat = ({ icon: Icon, label, value, hint, tint, testid }) => (
  <div className="rounded-lg border bg-card p-3" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
      {Icon && <Icon className={`w-3 h-3 ${tint || ''}`} />}
      {label}
    </div>
    <div className={`text-xl font-semibold mt-1 ${tint || ''}`}>{value ?? '—'}</div>
    {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
  </div>
);

// ---------------------------------------------------------------------------
// CollapsibleSection — used across the Profile so every scrollable card lands
// collapsed by default with a "summary line" of key metrics visible. The
// chevron + amber tint of the toggle button is the user's "highlighted expand
// mark" so it never gets lost.
// ---------------------------------------------------------------------------
const CollapsibleSection = ({
  icon: Icon, title, summary, badge, defaultOpen = false,
  rightSlot, testid, children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-muted/30 transition-colors"
        data-testid={testid ? `${testid}-toggle` : undefined}
        aria-expanded={open}
      >
        {Icon && <Icon className="w-4 h-4 text-primary shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold flex items-center gap-2">
            {title}
            {badge != null && <Badge variant="outline" className="text-[10px]">{badge}</Badge>}
          </div>
          {summary && <div className="text-[11px] text-muted-foreground truncate">{summary}</div>}
        </div>
        {rightSlot}
        <span className={`shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full border ${open ? 'bg-amber-100 border-amber-300 text-amber-900' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>
      {open && <CardContent className="pt-0">{children}</CardContent>}
    </Card>
  );
};

// --- Team section for supervisors & super admins -----------------------------
const TeamSection = ({ period, navigate, mySelfId, caps }) => {
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rescue, setRescue] = useState(null); // teammate currently being "rescued"
  const reloadTeam = async () => {
    try {
      const res = await fetch(`${API}/pollen/performance/team?period=${period}`, {
        credentials: 'include', headers: authHeaders(),
      });
      if (res.ok) setTeam(await res.json());
    } catch { /* keep prior state */ }
  };
  useEffect(() => {
    setLoading(true);
    reloadTeam().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  if (loading) {
    return <Card><CardContent className="p-4 text-xs text-muted-foreground">Loading team…</CardContent></Card>;
  }
  if (!team || !team.members || team.members.length <= 1) return null;
  const others = team.members.filter(m => m.user_id !== mySelfId);
  if (others.length === 0) return null;
  const totalOpen = others.reduce((s, m) => s + (m.sales?.tasks_open ?? 0), 0);

  return (
    <>
    <CollapsibleSection
      icon={Users}
      title="Team I can support"
      summary={`${others.length} teammate${others.length === 1 ? '' : 's'} · ${totalOpen} open task${totalOpen === 1 ? '' : 's'} you could help with`}
      badge={others.length}
      testid="team-pulse-section"
      rightSlot={caps.is_super_admin ? <span className="text-[10px] text-amber-700 inline-flex items-center gap-1 mr-1"><CheckCircle2 className="w-3 h-3" />Super Admin</span> : null}
    >
      <div className="space-y-2 pt-1">
        <p className="text-[11px] text-muted-foreground mb-1">
          Tap any teammate to view their full Pulse · tap the amber badge to take over an open task in one tap.
        </p>
        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
        {others.map(m => {
          const open = m.sales?.tasks_open ?? 0;
          const givenOut = m.sales?.handoffs_given ?? 0;
          return (
            <div
              key={m.user_id}
              className="rounded-lg border bg-muted/20 hover:bg-muted/40 transition-colors p-3"
              data-testid={`team-pulse-row-${m.user_id}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => navigate(`/pollen-dots/profile/${m.user_id}`)}
                  className="flex-1 text-left min-w-0"
                  data-testid={`team-pulse-view-${m.user_id}`}
                >
                  <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                    {m.name || m.email}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">{m.email}</div>
                </button>
                {open > 0 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setRescue(m); }}
                    className="inline-flex items-center gap-1 px-2 h-6 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-900 text-[11px] font-semibold border border-amber-300 transition-colors shrink-0"
                    data-testid={`team-open-tasks-${m.user_id}`}
                    title="Tap to take over an open task"
                  >
                    <HandHelping className="w-3 h-3" />
                    {open} open
                  </button>
                )}
                <Badge variant="outline" className="text-[10px] shrink-0">{m.role || '—'}</Badge>
                <button
                  type="button"
                  onClick={() => navigate(`/pollen-dots/profile/${m.user_id}`)}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid={`team-pulse-arrow-${m.user_id}`}
                  aria-label="Open profile"
                >
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <MiniStat label="Tasks done" value={m.sales?.tasks_completed ?? 0} />
                <MiniStat label="TAT" value={m.sales?.avg_task_tat_hours != null ? `${m.sales.avg_task_tat_hours}h` : '—'} />
                <MiniStat
                  label="Handed off"
                  value={givenOut}
                  hint={givenOut > 0 ? 'finished by teammates' : ''}
                />
                <MiniStat label="Tours live" value={m.operations?.tours_in_progress ?? 0} />
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </CollapsibleSection>
    <RescueSheet member={rescue} onClose={() => setRescue(null)} onAfterTakeover={reloadTeam} caps={caps} navigate={navigate} />
    </>
  );
};

// --- One-tap rescue sheet ----------------------------------------------------
// Lists the teammate's open tasks and lets the supervisor reassign any of them
// to themselves. The backend records the handoff in `assignment_history` and
// credits whoever completes the task with `handoffs_received` — exactly the
// cooperative loop we wanted.
const RescueSheet = ({ member, onClose, onAfterTakeover, caps, navigate }) => {
  const [tasks, setTasks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [takingId, setTakingId] = useState(null);

  useEffect(() => {
    if (!member) { setTasks(null); return; }
    setLoading(true);
    (async () => {
      try {
        const url = `${API}/pollen/queries/tasks/open-by-assignee?assignee=${encodeURIComponent(member.email)}`;
        const res = await fetch(url, { credentials: 'include', headers: authHeaders() });
        if (res.ok) {
          const data = await res.json();
          setTasks(data.tasks || []);
        } else { setTasks([]); }
      } catch { setTasks([]); }
      setLoading(false);
    })();
  }, [member]);

  const takeOver = async (t) => {
    setTakingId(t.id);
    try {
      const res = await fetch(`${API}/pollen/queries/${t.query_id}/tasks/${t.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ assigned_to: caps.email }),
      });
      if (!res.ok) {
        toast.error('Could not reassign — try again.');
        return;
      }
      setTasks(prev => (prev || []).filter(x => x.id !== t.id));
      toast.success(`Took over "${t.task_type}" from ${member.name || member.email}`);
      onAfterTakeover && onAfterTakeover();
    } finally {
      setTakingId(null);
    }
  };

  return (
    <Sheet open={!!member} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto" data-testid="rescue-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-base">
            <HandHelping className="w-4 h-4 text-amber-700" />
            Take over a task from {member?.name || member?.email}
          </SheetTitle>
          <SheetDescription className="text-[11px]">
            One tap reassigns the task to you and logs a handoff in the timeline. The teammate keeps credit for the work they've already done — you'll get credit when you finish it.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-3 space-y-2">
          {loading && <div className="text-xs text-muted-foreground py-4 text-center">Loading open tasks…</div>}
          {!loading && tasks && tasks.length === 0 && (
            <div className="text-xs text-muted-foreground py-6 text-center inline-flex flex-col items-center gap-1 w-full">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              All clear — no open tasks right now.
            </div>
          )}
          {!loading && tasks && tasks.map(t => (
            <div key={t.id} className="rounded-lg border bg-white p-3 flex items-center gap-2" data-testid={`rescue-task-${t.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate flex items-center gap-1.5">
                  {t.task_type}
                  {t.priority && t.priority !== 'Normal' && (
                    <span className={`text-[10px] px-1.5 h-4 inline-flex items-center rounded-full text-white ${
                      t.priority === 'Urgent' ? 'bg-rose-600' : t.priority === 'High' ? 'bg-amber-500' : 'bg-slate-400'
                    }`}>{t.priority}</span>
                  )}
                  <Badge variant="outline" className="text-[10px]">{t.status}</Badge>
                </div>
                <button
                  type="button"
                  onClick={() => { onClose(); navigate(`/pollen-dots/queries/${t.query_id}`); }}
                  className="text-[11px] text-muted-foreground hover:text-foreground truncate underline-offset-2 hover:underline"
                >
                  {t.query_id_human || 'Query'} · {t.customer_name || '—'}
                </button>
              </div>
              <Button
                size="sm"
                className="h-8 text-xs"
                disabled={takingId === t.id}
                onClick={() => takeOver(t)}
                data-testid={`rescue-takeover-${t.id}`}
              >
                {takingId === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Take over'}
              </Button>
            </div>
          ))}
          {!loading && tasks === null && (
            <div className="text-xs text-rose-600 inline-flex items-center gap-1 py-4">
              <AlertCircle className="w-3.5 h-3.5" />Couldn't load tasks. Try closing and reopening this sheet.
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const MiniStat = ({ label, value, hint }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="text-sm font-semibold tabular-nums">{value}</div>
    {hint && <div className="text-[9px] text-muted-foreground leading-tight">{hint}</div>}
  </div>
);

// Super Admin–only: pick which Admin supervises which teammates.
const SupervisorManager = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/pollen/admin/supervises-options`, {
        credentials: 'include', headers: authHeaders(),
      });
      if (res.ok) setRows(await res.json());
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const admins = rows.filter(r => ['Admin', 'Super Admin'].includes(r.role));

  const toggle = async (admin, member) => {
    setSaving(true);
    try {
      const cur = new Set(admin.supervises || []);
      if (cur.has(member.user_id)) cur.delete(member.user_id);
      else cur.add(member.user_id);
      const res = await fetch(`${API}/pollen/admin/users/${admin.user_id}/supervises`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ supervises: Array.from(cur) }),
      });
      if (res.ok) load();
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="supervisor-manager">
      <p className="text-[11px] text-muted-foreground mb-2">
        Mark which Admins can support which teammates. Strictly for coaching &amp; support — never for ranking.
      </p>
      {loading ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : (
        <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
          {admins.length === 0 && (
            <p className="text-xs text-muted-foreground py-3 text-center">No Admins yet — add Admin users first under User Management.</p>
          )}
          {admins.map(a => (
            <div key={a.user_id} className="rounded-lg border bg-muted/30 p-2.5">
              <button
                type="button"
                onClick={() => setOpenId(o => o === a.user_id ? null : a.user_id)}
                className="w-full flex items-center justify-between text-left"
                data-testid={`supervisor-row-${a.user_id}`}
              >
                <div>
                  <div className="text-sm font-medium">{a.name || a.email}</div>
                  <div className="text-[11px] text-muted-foreground">{a.email}</div>
                </div>
                <Badge variant="outline" className="text-[10px]">
                  Supports {(a.supervises || []).length}
                </Badge>
              </button>
              {openId === a.user_id && (
                <div className="mt-2 grid grid-cols-1 gap-1 pt-2 border-t">
                  {rows.filter(r => r.user_id !== a.user_id).map(m => {
                    const on = (a.supervises || []).includes(m.user_id);
                    return (
                      <label key={m.user_id} className="flex items-center gap-2 text-xs py-1 cursor-pointer">
                        <input
                          type="checkbox" checked={on} disabled={saving}
                          onChange={() => toggle(a, m)}
                          data-testid={`supervises-${a.user_id}-${m.user_id}`}
                        />
                        <span className="flex-1 truncate">{m.name || m.email}</span>
                        <Badge variant="outline" className="text-[9px]">{m.role}</Badge>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// --- Contributions tabs (own profile) ---------------------------------------
const ContribTabs = ({ data, caps, navigate }) => {
  const [tab, setTab] = useState('tours');
  const [q, setQ] = useState('');
  useEffect(() => setQ(''), [tab]);

  const filterBy = (items, fields) => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter(it => fields.some(f => (it[f] || '').toString().toLowerCase().includes(term)));
  };
  const filteredTours     = useMemo(() => filterBy(data.tours || [],     ['tour_name', 'destination', 'customer_name']), [data.tours, q]);
  const filteredVendors   = useMemo(() => filterBy(data.vendors || [],   ['name', 'type', 'location']), [data.vendors, q]);
  const filteredCustomers = useMemo(() => filterBy(data.customers || [], ['name', 'phone', 'email']), [data.customers, q]);
  const filteredLocations = useMemo(() => filterBy(data.locations || [], ['name', 'type']), [data.locations, q]);

  const counts = {
    tours: data.tours?.length || 0,
    vendors: data.vendors?.length || 0,
    customers: data.customers?.length || 0,
    locations: data.locations?.length || 0,
  };
  const tabsDef = [
    { key: 'tours',     label: 'Tours',     icon: Compass,   count: counts.tours },
    { key: 'vendors',   label: 'Vendors',   icon: Building2, count: counts.vendors },
    { key: 'customers', label: 'Customers', icon: Users,     count: counts.customers },
    { key: 'locations', label: 'Locations', icon: MapPin,    count: counts.locations },
  ];

  return (
    <CollapsibleSection
      icon={Compass}
      title="My Contributions"
      summary={`${counts.tours} tours · ${counts.vendors} vendors · ${counts.customers} customers · ${counts.locations} locations`}
      badge={counts.tours + counts.vendors + counts.customers + counts.locations}
      testid="my-contrib-tabs"
    >
      <div className="pt-1 space-y-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-4 h-11 w-full p-1 gap-1 bg-muted/60">
            {tabsDef.map(t => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <TabsTrigger key={t.key} value={t.key}
                  className="flex items-center justify-center gap-1.5 sm:gap-2 h-9 rounded-md text-xs sm:text-sm font-medium data-[state=active]:shadow-sm"
                  data-testid={`my-tab-${t.key}`}>
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="hidden sm:inline truncate">{t.label}</span>
                  <span className={`ml-0.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] tabular-nums border ${
                      active ? 'bg-primary/10 text-primary border-primary/20' : 'bg-white text-muted-foreground border-border'
                    }`}>
                    {t.count}
                  </span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          <div className="relative mt-3">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-8 h-9 text-sm" placeholder={`Search your ${tab}…`}
              value={q} onChange={e => setQ(e.target.value)} data-testid="my-tab-search" />
          </div>

          <TabsContent value="tours" className="mt-3">
            <ScrollList emptyText="No tours created in this period." data-testid="list-tours">
              {filteredTours.map(t => (
                <ListRow key={t.id} data-testid={`my-tour-${t.id}`} onClick={() => navigate(`/pollen-dots/tour/${t.id}`)}
                  primary={t.tour_name || '—'}
                  secondary={`${t.destination || ''}${t.destination ? ' · ' : ''}${formatDate(t.start_date)} → ${formatDate(t.end_date)}`}
                  meta={formatDate(t.created_at?.slice(0, 10))} />
              ))}
            </ScrollList>
          </TabsContent>
          <TabsContent value="vendors" className="mt-3">
            <ScrollList emptyText="No vendors created in this period." data-testid="list-vendors">
              {filteredVendors.map(v => (
                <ListRow key={v.id} data-testid={`my-vendor-${v.id}`} onClick={() => navigate(`/pollen-dots/vendor/${v.id}`)}
                  primary={v.name}
                  secondary={`${v.type}${v.location ? ' · ' + v.location : ''}`}
                  meta={formatDate(v.created_at?.slice(0, 10))} />
              ))}
            </ScrollList>
          </TabsContent>
          <TabsContent value="customers" className="mt-3">
            <ScrollList emptyText="No customers created in this period." data-testid="list-customers">
              {filteredCustomers.map(c => (
                <ListRow key={c.id} data-testid={`my-customer-${c.id}`} onClick={() => navigate(`/pollen-dots/customer/${c.id}`)}
                  primary={c.name}
                  secondary={c.phone || c.email || '—'}
                  meta={formatDate(c.created_at?.slice(0, 10))} />
              ))}
            </ScrollList>
          </TabsContent>
          <TabsContent value="locations" className="mt-3">
            <ScrollList emptyText="No locations created in this period." data-testid="list-locations">
              {filteredLocations.map(l => (
                <ListRow key={l.id} data-testid={`my-location-${l.id}`}
                  primary={l.name}
                  secondary={`${l.type} · used ${l.usage_count || 0}×`}
                  meta={formatDate(l.created_at?.slice(0, 10))} />
              ))}
            </ScrollList>
          </TabsContent>
        </Tabs>
      </div>
    </CollapsibleSection>
  );
};

const ScrollList = ({ children, emptyText, ...rest }) => {
  const hasChildren = React.Children.count(children) > 0;
  return (
    <div className="rounded-md border bg-white max-h-[420px] overflow-y-auto" {...rest}>
      {hasChildren ? <div className="divide-y">{children}</div>
        : <p className="text-sm text-muted-foreground text-center py-10">{emptyText}</p>}
    </div>
  );
};

const ListRow = ({ primary, secondary, meta, onClick, ...rest }) => (
  <button type="button" onClick={onClick}
    className="w-full text-left px-3 py-2.5 hover:bg-muted/50 transition-colors flex items-center gap-3"
    {...rest}>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium truncate">{primary}</p>
      {secondary && <p className="text-[11px] text-muted-foreground truncate">{secondary}</p>}
    </div>
    {meta && <span className="text-[11px] text-muted-foreground whitespace-nowrap">{meta}</span>}
    {onClick && <ArrowRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
  </button>
);

const ActivityHistory = ({ activity }) => {
  const [open, setOpen] = useState(false);
  return (
    <Card data-testid="activity-history-card">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/30"
        data-testid="activity-history-toggle">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-rose-600" />
          <span className="text-sm font-medium">Activity History</span>
          <Badge variant="outline" className="text-[10px]">{activity?.length || 0}</Badge>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && (
        <CardContent className="pt-0">
          {(!activity || activity.length === 0) ? (
            <p className="text-sm text-muted-foreground text-center py-4">No activity in this period.</p>
          ) : (
            <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1" data-testid="activity-list">
              {activity.map((a, i) => (
                <div key={i} className="flex items-start gap-2 text-xs pb-2 border-b last:border-0">
                  <Clock className="w-3 h-3 text-muted-foreground mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className="text-[10px] h-4 px-1">{a.action}</Badge>
                      <span className="text-muted-foreground">{a.entity_type}</span>
                    </div>
                    <p className="text-[11px] truncate mt-0.5">{a.details || '—'}</p>
                    <p className="text-[10px] text-muted-foreground">{formatDate(a.timestamp?.slice(0, 10))} · {a.timestamp?.slice(11, 16)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

const SummaryTile = ({ icon: Icon, label, value, tint }) => (
  <Card>
    <CardContent className="p-3">
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${tint}`} />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <div className="text-xl font-bold mt-1">{value}</div>
    </CardContent>
  </Card>
);

// --- Listing assignments card -----------------------------------------------
// Surfaces Hotel / Driver / Guide / Operator / Location populator metrics for
// the user being viewed. Each block shows created count, missing-field count,
// and a flag list so the team knows what still needs to be filled in.
// Super Admins and supervisors get an "Assign Task" button to spin up a new
// populator assignment for any teammate.
const LISTING_ICONS = {
  Hotel: HotelIcon, Driver: Car, Guide: UserCheck, Operator: Megaphone, Location: Globe,
};

// Visual countdown chip for `due_at`. Renders green when on track,
// amber when within 24h, rose-red when overdue.
const DueChip = ({ iso }) => {
  const target = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = target - now;
  const overdue = diffMs < 0;
  const hours = Math.abs(diffMs) / 36e5;
  let label;
  if (overdue) {
    label = hours < 24 ? `${Math.round(hours)}h overdue` : `${Math.round(hours / 24)}d overdue`;
  } else if (hours < 1) {
    label = `${Math.max(1, Math.round(diffMs / 60000))} min left`;
  } else if (hours < 48) {
    label = `${Math.round(hours)}h left`;
  } else {
    label = `${Math.round(hours / 24)}d left`;
  }
  const cls = overdue
    ? 'bg-rose-100 text-rose-900 border-rose-300'
    : hours < 24
      ? 'bg-amber-100 text-amber-900 border-amber-300'
      : 'bg-emerald-50 text-emerald-800 border-emerald-200';
  return (
    <span className={`text-[10px] px-1.5 h-4 inline-flex items-center rounded-full border font-medium ${cls}`} title={`Due ${new Date(iso).toLocaleString()}`}>
      <Clock className="w-2.5 h-2.5 mr-0.5" />{label}
    </span>
  );
};

const ListingsCard = ({ listings, isOwn, canAssign, viewingEmail, onChange, caps }) => {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [openType, setOpenType] = useState(null); // expanded flag list
  const [expandedBlock, setExpandedBlock] = useState(null); // per-entity collapse
  const [marking, setMarking] = useState(null);
  const [editing, setEditing] = useState(null);
  const [shareRow, setShareRow] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [people, setPeople] = useState([]);
  const entityTypes = ['Hotel', 'Driver', 'Guide', 'Operator', 'Location'];
  const hasAny = Object.values(listings || {}).some(v => (v?.created_total || 0) > 0 || v?.assignment_status);

  useEffect(() => {
    if (!canAssign) return;
    (async () => {
      try {
        const r = await fetch(`${API}/pollen/admin/supervises-options`, { credentials: 'include', headers: authHeaders() });
        if (r.ok) setPeople(await r.json());
      } catch { /* noop */ }
    })();
  }, [canAssign]);

  // Roll-up summary for the parent CollapsibleSection header.
  const totalAssignments = Object.values(listings || {}).filter(v => v?.assignment_status === 'Active').length;
  const totalMissing = Object.values(listings || {}).reduce((s, v) => s + (v?.missing_critical || 0), 0);
  const totalLifetime = Object.values(listings || {}).reduce((s, v) => s + (v?.created_total || 0), 0);

  // Deeplink a flagged record straight into its edit form so the team
  // member can clear the missing field in one tap.
  const editLinkFor = (entityType, id) => {
    if (entityType === 'Location') return `/pollen-dots/locations?focus=${id}`;
    // Hotel / Driver / Guide / Operator all live in pollen_vendors.
    return `/pollen-dots/vendor/${id}`;
  };
  const goEdit = (entityType, id) => navigate(editLinkFor(entityType, id));

  const markComplete = async (aid) => {
    setMarking(aid);
    try {
      const res = await fetch(`${API}/pollen/listing-assignments/${aid}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ status: 'Completed' }),
      });
      if (res.ok) {
        toast.success('Marked complete — TAT locked in.');
        onChange && onChange();
      } else {
        toast.error('Could not mark complete.');
      }
    } finally { setMarking(null); }
  };

  const deleteAssignment = async (aid) => {
    const res = await fetch(`${API}/pollen/listing-assignments/${aid}`, {
      method: 'DELETE', credentials: 'include', headers: authHeaders(),
    });
    if (res.ok) { toast.success('Task deleted'); setConfirmDel(null); onChange && onChange(); }
    else { toast.error('Only the creator can delete this task'); }
  };

  // Build a synthetic "row" matching the TaskActions/EditDialog/ShareSheet
  // shape from a listings block.
  const rowFromBlock = (entityType, data) => ({
    id: data.assignment_id,
    entity_type: entityType,
    assigned_to: viewingEmail,
    assigned_by: data.assigned_by,
    target: data.target,
    due_at: data.due_at,
    notes: '',
    status: data.assignment_status,
    created_at: data.assignment_created_at,
  });

  return (
    <CollapsibleSection
      icon={ClipboardList}
      title="Database Listings"
      summary={hasAny
        ? `${totalAssignments} active assignment${totalAssignments === 1 ? '' : 's'} · ${totalLifetime} created · ${totalMissing} missing field${totalMissing === 1 ? '' : 's'}`
        : 'No populator assignments yet.'}
      badge={hasAny ? 'populator' : null}
      testid="listings-card"
      defaultOpen={hasAny && totalAssignments > 0}
      rightSlot={canAssign ? (
        <Button
          size="sm"
          className="h-8 text-xs"
          onClick={(e) => { e.stopPropagation(); setDialogOpen(true); }}
          data-testid="assign-listing-task-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1" />Assign Task
        </Button>
      ) : null}
    >
      <div className="space-y-2 pt-1">
        {!hasAny && (
          <p className="text-[11px] text-muted-foreground text-center py-3">
            {canAssign
              ? 'Tap "Assign Task" to put a teammate on Hotel / Driver / Guide / Operator / Location population.'
              : 'No populator assignments yet.'}
          </p>
        )}
        {entityTypes.map(t => {
          const data = listings?.[t];
          if (!data) return null;
          const Icon = LISTING_ICONS[t] || ClipboardList;
          const expanded = openType === t;
          const blockOpen = expandedBlock === t;
          const completePct = data.created_total
            ? Math.round(((data.created_total - data.missing_critical) / data.created_total) * 100)
            : 0;
          const canMark = data.assignment_status === 'Active' && data.assignment_id && (
            isOwn
              || caps?.is_super_admin
              || caps?.is_supervisor
              || (data.assigned_by || '').toLowerCase() === (caps?.email || '').toLowerCase()
          );
          // Listings created since the assignment began — drives the
          // progress bar. We rely on baseline_count (snapshot at assign
          // time) so prior entries don't get credited.
          const progress = data.baseline_count != null
            ? Math.max(0, (data.created_total || 0) - data.baseline_count)
            : (data.created_total || 0);
          const progressPct = data.target
            ? Math.min(100, Math.round((progress / data.target) * 100))
            : 0;
          return (
            <div key={t} className="rounded-lg border bg-muted/20" data-testid={`listing-block-${t}`}>
              {/* Collapsed summary row — always visible */}
              <button
                type="button"
                onClick={() => setExpandedBlock(blockOpen ? null : t)}
                className="w-full flex items-center gap-2 p-3 text-left hover:bg-muted/40 transition-colors"
                data-testid={`listing-block-toggle-${t}`}
                aria-expanded={blockOpen}
              >
                <Icon className="w-4 h-4 text-amber-700 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                    {t}s
                    {data.assignment_status && (
                      <Badge
                        variant="outline"
                        className={`text-[10px] ${data.assignment_status === 'Active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : data.assignment_status === 'Completed' ? 'bg-sky-50 text-sky-700 border-sky-200' : ''}`}
                      >{data.assignment_status}{data.target ? ` · target ${data.target}` : ''}</Badge>
                    )}
                    {data.due_at && <DueChip iso={data.due_at} />}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {data.created_total} created · {data.missing_critical} missing field{data.missing_critical === 1 ? '' : 's'}
                    {data.created_total ? ` · ${completePct}% complete` : ''}
                  </div>
                </div>
                <span className={`shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full border ${blockOpen ? 'bg-amber-100 border-amber-300 text-amber-900' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                  {blockOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </span>
              </button>
              {blockOpen && (
                <div className="px-3 pb-3 pt-1 space-y-2 border-t bg-white/50">
                  {data.assigned_by && data.assignment_status && (
                    <div className="text-[10px] text-muted-foreground">
                      Assigned by {data.assigned_by}
                      {data.assignment_created_at && ` · ${formatDate(data.assignment_created_at.slice(0, 10))}`}
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-2">
                    <MiniStat label="In period" value={data.created_in_period ?? 0} />
                    <MiniStat label="Lifetime" value={data.created_total ?? 0} />
                    <MiniStat
                      label="Missing fields"
                      value={data.missing_critical ?? 0}
                      hint={data.created_total ? `${completePct}% complete` : ''}
                    />
                  </div>
                  {data.assignment_status === 'Active' && data.target && (
                    <div data-testid={`listing-progress-${t}`}>
                      <div className="flex items-center justify-between text-[11px] mb-0.5">
                        <span className="text-muted-foreground">Task progress</span>
                        <span className="font-semibold tabular-nums">{progress} / {data.target} · {progressPct}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className={`h-full rounded-full ${progressPct >= 100 ? 'bg-emerald-500' : 'bg-amber-500'}`}
                          style={{ width: `${progressPct}%` }}
                        />
                      </div>
                    </div>
                  )}
                  {canMark && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full h-8 text-xs border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                      disabled={marking === data.assignment_id}
                      onClick={() => markComplete(data.assignment_id)}
                      data-testid={`listing-mark-complete-${t}`}
                    >
                      {marking === data.assignment_id
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Mark task complete · lock TAT</>}
                    </Button>
                  )}
                  {data.assignment_id && (
                    <div className="flex items-center justify-end">
                      <TaskActions
                        row={rowFromBlock(t, data)}
                        myEmail={caps?.email || ''}
                        isSuper={!!caps?.is_super_admin}
                        onEdit={() => setEditing(rowFromBlock(t, data))}
                        onShare={() => setShareRow(rowFromBlock(t, data))}
                        onDelete={() => setConfirmDel(rowFromBlock(t, data))}
                      />
                    </div>
                  )}
                  {(data.flags || []).length > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={() => setOpenType(expanded ? null : t)}
                        className="text-[11px] text-amber-800 hover:text-amber-900 inline-flex items-center gap-1"
                        data-testid={`listing-flags-toggle-${t}`}
                      >
                        <AlertCircle className="w-3 h-3" />
                        {expanded ? 'Hide flagged entries' : `Show ${data.missing_critical} flagged entr${data.missing_critical === 1 ? 'y' : 'ies'}`}
                        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </button>
                      {expanded && (
                        <div className="rounded-md border bg-white max-h-[260px] overflow-y-auto divide-y">
                          {data.flags.map(f => (
                            <button
                              type="button"
                              key={f.id}
                              onClick={() => goEdit(t, f.id)}
                              className="w-full text-left px-2.5 py-2 text-xs flex items-start gap-2 hover:bg-amber-50 transition-colors"
                              data-testid={`listing-flag-${f.id}`}
                              title="Tap to open this record's edit form"
                            >
                              <AlertCircle className="w-3 h-3 text-amber-600 mt-0.5 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{f.name}</div>
                                <div className="text-[10px] text-muted-foreground">Missing: {f.missing.join(', ')}</div>
                              </div>
                              <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0 mt-0.5" />
                            </button>
                          ))}
                          {data.missing_critical > (data.flags || []).length && (
                            <div className="px-2.5 py-2 text-[10px] text-muted-foreground italic">
                              …and {data.missing_critical - data.flags.length} more flagged entries.
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <AssignListingDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        defaultAssignee={isOwn ? '' : viewingEmail}
        onCreated={() => { onChange && onChange(); }}
      />
      <EditAssignmentDialog open={!!editing} row={editing}
        onOpenChange={(o) => { if (!o) setEditing(null); }}
        onSaved={() => { setEditing(null); onChange && onChange(); }} />
      <ShareAssignmentSheet open={!!shareRow} row={shareRow} people={people}
        onClose={() => setShareRow(null)} />
      <Dialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <DialogContent className="max-w-sm" data-testid="delete-task-dialog">
          <DialogHeader>
            <DialogTitle>Delete this task?</DialogTitle>
            <DialogDescription className="text-[11px]">
              Only the creator can delete. The listings already created stay; only the assignment record is removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => deleteAssignment(confirmDel.id)} data-testid="confirm-delete-task">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CollapsibleSection>
  );
};

const AssignListingDialog = ({ open, onOpenChange, defaultAssignee, onCreated }) => {
  const [people, setPeople] = useState([]);
  const [entityType, setEntityType] = useState('Hotel');
  const [assignee, setAssignee] = useState(defaultAssignee || '');
  const [target, setTarget] = useState('');
  const [notes, setNotes] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('18:00');
  const [saving, setSaving] = useState(false);
  // After a successful assignment we switch the dialog into a small
  // confirmation panel offering a one-tap WhatsApp share with the team
  // member.
  const [confirm, setConfirm] = useState(null); // { entity_type, assignee, due_at, target, notes, name, phone }

  useEffect(() => {
    if (!open) { setConfirm(null); return; }
    setAssignee(defaultAssignee || '');
    setEntityType('Hotel'); setTarget(''); setNotes(''); setDueDate(''); setDueTime('18:00');
    setConfirm(null);
    (async () => {
      try {
        const res = await fetch(`${API}/pollen/admin/supervises-options`, {
          credentials: 'include', headers: authHeaders(),
        });
        if (res.ok) setPeople(await res.json());
      } catch { /* noop */ }
    })();
  }, [open, defaultAssignee]);

  const submit = async () => {
    if (!assignee) { toast.error('Pick a teammate'); return; }
    setSaving(true);
    try {
      // Build an ISO datetime if both date and time are provided.
      let dueIso = null;
      if (dueDate) {
        const t = dueTime || '18:00';
        // Use local-time interpretation so a 6 PM IST deadline reads as
        // 6 PM IST in the assignee's browser.
        dueIso = new Date(`${dueDate}T${t}`).toISOString();
      }
      const res = await fetch(`${API}/pollen/listing-assignments`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          entity_type: entityType,
          assigned_to: assignee,
          target: target ? Number(target) : null,
          due_at: dueIso,
          notes,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not assign');
        return;
      }
      const me = people.find(p => p.email === assignee);
      toast.success(`${entityType} populator assigned to ${me?.name || assignee}`);
      setConfirm({
        entity_type: entityType,
        assignee,
        due_at: dueIso,
        target: target ? Number(target) : null,
        notes,
        name: me?.name || '',
        phone: me?.phone || '',
        user_id: me?.user_id || '',
      });
      onCreated && onCreated();
    } finally { setSaving(false); }
  };

  // Build the public profile URL the teammate should open. We point them
  // at /pollen-dots/my so they land on their own Profile, where the new
  // assignment is already rendered in the Database Listings card.
  const profileUrl = `${window.location.origin}/pollen-dots/my`;

  const waLink = () => {
    if (!confirm) return '#';
    const due = confirm.due_at
      ? `\nDue: ${new Date(confirm.due_at).toLocaleString()}`
      : '';
    const tgt = confirm.target ? `\nTarget: ${confirm.target} listings` : '';
    const extra = confirm.notes ? `\nNotes: ${confirm.notes}` : '';
    const msg = `Hi ${confirm.name || ''}, I've assigned you a *${confirm.entity_type}* populator task on Pollen Dots.${tgt}${due}${extra}\n\nOpen your Profile to see the task & critical-field flags: ${profileUrl}`;
    // Prefer wa.me/<phone> when we have a phone, else fall back to a generic share.
    const phoneDigits = (confirm.phone || '').replace(/\D+/g, '');
    return phoneDigits
      ? `https://wa.me/${phoneDigits}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="assign-listing-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-amber-700" />
            {confirm ? 'Task assigned — share it' : 'Assign a populator task'}
          </DialogTitle>
          <DialogDescription className="text-[11px]">
            {confirm
              ? 'Send the link below to your teammate on WhatsApp so they can open the task on their profile.'
              : 'The assignee\'s Pulse will start tracking how many listings of this type they create, with critical-field flags (contact, location pin, rate chart, etc.) surfaced automatically.'}
          </DialogDescription>
        </DialogHeader>

        {!confirm && (
          <div className="space-y-3">
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Listing type</label>
              <Select value={entityType} onValueChange={setEntityType}>
                <SelectTrigger className="h-9" data-testid="assign-entity-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Hotel">Hotels</SelectItem>
                  <SelectItem value="Driver">Drivers</SelectItem>
                  <SelectItem value="Guide">Guides</SelectItem>
                  <SelectItem value="Operator">Operators</SelectItem>
                  <SelectItem value="Location">Locations</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Assignee</label>
              <Select value={assignee} onValueChange={setAssignee}>
                <SelectTrigger className="h-9" data-testid="assign-assignee"><SelectValue placeholder="Pick a teammate" /></SelectTrigger>
                <SelectContent>
                  {people.map(p => (
                    <SelectItem key={p.user_id} value={p.email}>
                      {p.name || p.email} · {p.role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Target (optional)</label>
                <Input type="number" min="1" value={target} onChange={(e) => setTarget(e.target.value)}
                  placeholder="e.g. 20" className="h-9" data-testid="assign-target" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Priority</label>
                <div className="h-9 flex items-end">
                  <span className="text-[10px] text-muted-foreground">Used for TAT tracking</span>
                </div>
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Target time/date</label>
              <div className="grid grid-cols-2 gap-2">
                <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                  className="h-9" data-testid="assign-due-date" />
                <Input type="time" value={dueTime} onChange={(e) => setDueTime(e.target.value)}
                  className="h-9" data-testid="assign-due-time" disabled={!dueDate} />
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Sets the deadline used to measure TAT vs target.</p>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Notes</label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Context, region focus, deadline…" className="text-xs" data-testid="assign-notes" />
            </div>
          </div>
        )}

        {confirm && (
          <div className="space-y-3" data-testid="assign-share-panel">
            <div className="rounded-lg border bg-emerald-50 border-emerald-200 p-3 text-xs">
              <div className="font-semibold text-emerald-900 mb-1">
                {confirm.entity_type} populator → {confirm.name || confirm.assignee}
              </div>
              {confirm.target && <div className="text-emerald-800">Target: {confirm.target} listings</div>}
              {confirm.due_at && <div className="text-emerald-800">Due: {new Date(confirm.due_at).toLocaleString()}</div>}
              {confirm.notes && <div className="text-emerald-800 truncate">Notes: {confirm.notes}</div>}
            </div>
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium"
              data-testid="assign-whatsapp-share"
            >
              <Megaphone className="w-4 h-4" />Send on WhatsApp
            </a>
            <p className="text-[10px] text-muted-foreground text-center">
              Or copy the link: <button
                type="button"
                onClick={() => { navigator.clipboard?.writeText(profileUrl); toast.success('Link copied'); }}
                className="underline" data-testid="assign-copy-link">{profileUrl}</button>
            </p>
          </div>
        )}

        <DialogFooter>
          {confirm ? (
            <Button onClick={() => onOpenChange(false)} data-testid="assign-share-close">Done</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={submit} disabled={saving || !assignee} data-testid="assign-submit">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Assign'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// --- Tasks I've Assigned (creator's inbox) ----------------------------------
// Lists every listing-assignment the caller created OR oversees (their
// supervisees' assignments). Each row has Share / Edit / Delete actions
// gated by permissions (only the creator can delete; supervisors can
// share + edit but not delete).
const TasksAssignedCard = ({ myEmail, period, navigate, caps, isSuper }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('Active');
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [people, setPeople] = useState([]);
  const [shareRow, setShareRow] = useState(null);
  // Bulk-delete affordance: pick a set of rows and wipe them in one call.
  const [picked, setPicked] = useState(new Set());
  const [bulkDelOpen, setBulkDelOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const reload = async () => {
    try {
      const res = await fetch(`${API}/pollen/listing-assignments?status=`, {
        credentials: 'include', headers: authHeaders(),
      });
      const listingRows = res.ok ? await res.json() : [];

      // Also include vendor-data-gap tasks assigned by me. Normalise them
      // into the same shape used by the populator listings so the UI can
      // render them without a second card.
      let gapRows = [];
      try {
        const gr = await fetch(`${API}/pollen/data-quality/tasks?assigned_by=me`, {
          credentials: 'include', headers: authHeaders(),
        });
        if (gr.ok) gapRows = (await gr.json()).map(t => ({
          id: t.id,
          kind: 'vendor-data-gap',
          entity_type: t.field === 'phone' ? 'Contact'
                     : t.field === 'pin' ? 'Map pin'
                     : t.field === 'email' ? 'Email'
                     : 'Location',
          target: (t.vendor_ids || []).length,
          done_count: t.done_count ?? 0,
          total_count: t.total_count ?? (t.vendor_ids || []).length,
          assigned_to: t.assigned_to_name || t.assigned_to_email,
          assigned_to_email: t.assigned_to_email,
          assigned_by: t.assigned_by_name || t.assigned_by_email,
          assigned_by_email: t.assigned_by_email,
          status: t.status === 'Done' ? 'Completed' : 'Active',
          due_at: t.due_at || null,
          notes: t.note || t.title || '',
          created_at: t.created_at,
          gap_field: t.field,
          deep_link: `/pollen-dots-dashboard?gap=${t.field}&task=${encodeURIComponent(t.id)}`,
        }));
      } catch { /* noop */ }

      setRows([...listingRows, ...gapRows]);
    } catch { /* noop */ }
    setLoading(false);
  };
  useEffect(() => {
    if (!myEmail) { setLoading(false); return; }
    setLoading(true); reload();
    (async () => {
      try {
        const r = await fetch(`${API}/pollen/admin/supervises-options`, { credentials: 'include', headers: authHeaders() });
        if (r.ok) setPeople(await r.json());
      } catch { /* noop */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myEmail, period]);

  const active = rows.filter(r => r.status === 'Active');
  const completed = rows.filter(r => r.status === 'Completed');
  const shown = tab === 'Active' ? active : completed;
  const summary = `${active.length} active · ${completed.length} completed`;

  const onDelete = async (id) => {
    // Route to the correct collection based on task kind — data-gap tasks
    // live in pollen_data_gap_tasks and have their own delete endpoint.
    const row = rows.find(r => r.id === id);
    const isGap = row?.kind === 'vendor-data-gap';
    const url = isGap
      ? `${API}/pollen/data-quality/tasks/${id}`
      : `${API}/pollen/listing-assignments/${id}`;
    const res = await fetch(url, {
      method: 'DELETE', credentials: 'include', headers: authHeaders(),
    });
    if (res.ok) { toast.success('Task deleted'); setConfirmDel(null); reload(); }
    else {
      const err = await res.text().catch(() => '');
      toast.error(err && err.length < 120 ? err : 'Only the creator or Super Admin can delete this task');
    }
  };

  if (loading) return null;
  if (rows.length === 0) return null;

  return (
    <>
    <CollapsibleSection
      icon={Megaphone}
      title="Tasks I oversee"
      summary={summary}
      badge={rows.length}
      testid="tasks-assigned-card"
    >
      <div className="space-y-2 pt-1">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-9">
            <TabsTrigger value="Active" className="text-xs px-3" data-testid="tasks-assigned-active">Active · {active.length}</TabsTrigger>
            <TabsTrigger value="Completed" className="text-xs px-3" data-testid="tasks-assigned-completed">Completed · {completed.length}</TabsTrigger>
          </TabsList>
        </Tabs>
        {/* Bulk toolbar — appears once at least one row is picked. Useful
            after a giant bulk-assign you want to undo in one tap. */}
        {picked.size > 0 && (
          <div className="flex items-center justify-between gap-2 p-2 rounded-md border border-amber-300 bg-amber-50 text-xs text-amber-900" data-testid="bulk-task-toolbar">
            <span>{picked.size} selected</span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setPicked(new Set())} data-testid="bulk-clear">Clear</Button>
              <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => setBulkDelOpen(true)} data-testid="bulk-delete-open">
                <Trash2 className="w-3.5 h-3.5 mr-1" />Delete selected
              </Button>
            </div>
          </div>
        )}
        <div className="rounded-md border bg-white max-h-[420px] overflow-y-auto divide-y">
          {shown.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-6">
              {tab === 'Active' ? 'No active tasks right now.' : 'No completed tasks yet.'}
            </p>
          )}
          {shown.map(r => (
            <div key={r.id} className="px-3 py-2.5 text-xs" data-testid={`tasks-assigned-row-${r.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="checkbox"
                  checked={picked.has(r.id)}
                  onChange={() => setPicked(prev => {
                    const next = new Set(prev);
                    if (next.has(r.id)) next.delete(r.id); else next.add(r.id);
                    return next;
                  })}
                  className="h-3.5 w-3.5 mr-1 accent-amber-600"
                  data-testid={`bulk-pick-${r.id}`}
                />
                <span className="font-semibold">{r.entity_type}s</span>
                <span className="text-muted-foreground">→</span>
                <span className="font-medium truncate" data-testid={`tasks-assignee-${r.id}`}>{r.assigned_to}</span>
                {(r.kind === 'vendor-data-gap' || (Number.isFinite(r.done_count) && (r.total_count || 0) > 0)) ? (
                  <Badge
                    variant="outline"
                    className={`text-[10px] tabular-nums ${(r.done_count || 0) >= (r.total_count || 0) && (r.total_count || 0) > 0
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                      : 'bg-amber-50 text-amber-900 border-amber-200'}`}
                    data-testid={`task-progress-${r.id}`}
                  >
                    {(r.done_count || 0) >= (r.total_count || 0) && (r.total_count || 0) > 0 && (
                      <CheckCircle2 className="w-3 h-3 mr-0.5" />
                    )}
                    {r.done_count || 0} / {r.total_count || 0} done
                  </Badge>
                ) : (
                  r.target && <Badge variant="outline" className="text-[10px]">target {r.target}</Badge>
                )}
                {r.due_at && <DueChip iso={r.due_at} />}
                {r.status === 'Completed' && (
                  <Badge variant="outline" className="text-[10px] bg-sky-50 text-sky-700 border-sky-200">
                    <CheckCircle2 className="w-3 h-3 mr-0.5" />
                    Done {r.completed_at ? `· ${tatLabel(r.created_at, r.completed_at)}` : ''}
                  </Badge>
                )}
              </div>
              {r.notes && <div className="text-[11px] text-muted-foreground mt-1 truncate">"{r.notes}"</div>}
              <div className="flex items-center justify-between gap-2 mt-1.5">
                <div className="text-[10px] text-muted-foreground">
                  Created {formatDate((r.created_at || '').slice(0, 10))} by {r.assigned_by}
                </div>
                <TaskActions
                  row={r}
                  myEmail={myEmail}
                  isSuper={isSuper}
                  onEdit={() => setEditing(r)}
                  onShare={() => setShareRow(r)}
                  onDelete={() => setConfirmDel(r)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </CollapsibleSection>
    <EditAssignmentDialog open={!!editing} row={editing}
      onOpenChange={(o) => { if (!o) setEditing(null); }}
      onSaved={() => { setEditing(null); reload(); }} />
    <ShareAssignmentSheet open={!!shareRow} row={shareRow} people={people}
      onClose={() => setShareRow(null)} />
    <Dialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
      <DialogContent className="max-w-sm" data-testid="delete-task-dialog">
        <DialogHeader>
          <DialogTitle>Delete this task?</DialogTitle>
          <DialogDescription className="text-[11px]">
            Removes the {confirmDel?.entity_type} populator task for {confirmDel?.assigned_to}. The listings they've created stay; only the assignment record is removed.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setConfirmDel(null)}>Cancel</Button>
          <Button variant="destructive" onClick={() => onDelete(confirmDel.id)} data-testid="confirm-delete-task">Delete</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <Dialog open={bulkDelOpen} onOpenChange={(o) => { if (!o && !bulkBusy) setBulkDelOpen(false); }}>
      <DialogContent className="max-w-sm" data-testid="bulk-delete-dialog">
        <DialogHeader>
          <DialogTitle>Delete {picked.size} task(s)?</DialogTitle>
          <DialogDescription className="text-[11px]">
            Wipes all selected tasks in one shot. The listings or vendor data already filled stay intact — only the assignment rows are removed.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" disabled={bulkBusy} onClick={() => setBulkDelOpen(false)}>Cancel</Button>
          <Button
            variant="destructive"
            disabled={bulkBusy}
            data-testid="confirm-bulk-delete"
            onClick={async () => {
              setBulkBusy(true);
              const ids = Array.from(picked);
              let ok = 0, fail = 0;
              for (const id of ids) {
                const row = rows.find(r => r.id === id);
                const isGap = row?.kind === 'vendor-data-gap';
                const url = isGap ? `${API}/pollen/data-quality/tasks/${id}` : `${API}/pollen/listing-assignments/${id}`;
                try {
                  const r = await fetch(url, { method: 'DELETE', credentials: 'include', headers: authHeaders() });
                  if (r.ok) ok += 1; else fail += 1;
                } catch { fail += 1; }
              }
              setBulkBusy(false);
              setBulkDelOpen(false);
              setPicked(new Set());
              if (fail === 0) toast.success(`Deleted ${ok} task(s)`);
              else toast.error(`Deleted ${ok}, failed ${fail} (likely not yours)`);
              reload();
            }}
          >
            {bulkBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
            Delete selected
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

// --- Reusable per-row action cluster ---------------------------------------
// Share is universal. Edit needs creator/supervisor/super-admin. Delete is
// strictly creator/super-admin.
const TaskActions = ({ row, myEmail, isSuper, onEdit, onShare, onDelete }) => {
  if (!row) return null;
  const me = (myEmail || '').toLowerCase();
  const creator = (row.assigned_by || '').toLowerCase();
  const canEdit = isSuper || creator === me || row.__canEdit;
  const canDelete = isSuper || creator === me;
  return (
    <div className="flex items-center gap-1.5 shrink-0">
      {row.kind === 'vendor-data-gap' && row.gap_field && (
        <a
          href={`/pollen-dots-dashboard?gap=${row.gap_field}&task=${encodeURIComponent(row.id)}`}
          title="Open task — see progress with green ticks"
          className="inline-flex items-center justify-center w-7 h-7 rounded-full border bg-sky-50 border-sky-200 hover:bg-sky-100 text-sky-700"
          data-testid={`task-open-${row.id}`}
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
      {row.kind !== 'vendor-data-gap' && row.entity_type && (
        <a
          href={`/pollen-dots/vendors?type=${encodeURIComponent(row.entity_type)}&task=${encodeURIComponent(row.id)}`}
          title="Open the listing surface for this populator task"
          className="inline-flex items-center justify-center w-7 h-7 rounded-full border bg-sky-50 border-sky-200 hover:bg-sky-100 text-sky-700"
          data-testid={`task-open-${row.id}`}
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
      <button type="button" onClick={onShare} title="Share on WhatsApp"
        className="inline-flex items-center justify-center w-7 h-7 rounded-full border bg-emerald-50 border-emerald-200 hover:bg-emerald-100 text-emerald-700"
        data-testid={`task-share-${row.id}`}>
        <Megaphone className="w-3.5 h-3.5" />
      </button>
      {canEdit && (
        <button type="button" onClick={onEdit} title="Edit task"
          className="inline-flex items-center justify-center w-7 h-7 rounded-full border bg-amber-50 border-amber-200 hover:bg-amber-100 text-amber-700"
          data-testid={`task-edit-${row.id}`}>
          <Clock className="w-3.5 h-3.5" />
        </button>
      )}
      {canDelete && (
        <button type="button" onClick={onDelete} title="Delete task"
          className="inline-flex items-center justify-center w-7 h-7 rounded-full border bg-rose-50 border-rose-200 hover:bg-rose-100 text-rose-700"
          data-testid={`task-delete-${row.id}`}>
          <AlertCircle className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

// --- Edit dialog — reuses the same look as the assign dialog ----------------
const EditAssignmentDialog = ({ open, row, onOpenChange, onSaved }) => {
  const [target, setTarget] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('18:00');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!row) return;
    setTarget(row.target || '');
    if (row.due_at) {
      const d = new Date(row.due_at);
      setDueDate(d.toISOString().slice(0, 10));
      setDueTime(`${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`);
    } else { setDueDate(''); setDueTime('18:00'); }
    setNotes(row.notes || '');
  }, [row]);

  const save = async () => {
    setSaving(true);
    try {
      let dueIso = null;
      if (dueDate) {
        dueIso = new Date(`${dueDate}T${dueTime || '18:00'}`).toISOString();
      }
      // Route to the right endpoint based on task kind. data-gap tasks
      // don't carry an editable `target` (it's the count of vendor_ids)
      // — we only PATCH the editable fields: due_at, note, status.
      const isGap = row?.kind === 'vendor-data-gap';
      const url = isGap
        ? `${API}/pollen/data-quality/tasks/${row.id}`
        : `${API}/pollen/listing-assignments/${row.id}`;
      const body = isGap
        ? { due_at: dueIso, note: notes }
        : { target: target ? Number(target) : null, due_at: dueIso, notes };
      const res = await fetch(url, {
        method: isGap ? 'PATCH' : 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.text().catch(() => '');
        toast.error(err && err.length < 120 ? err : 'Could not update');
        return;
      }
      toast.success('Task updated');
      onSaved && onSaved();
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="edit-task-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Edit task</DialogTitle>
          <DialogDescription className="text-[11px]">
            {row?.entity_type}s populator for {row?.assigned_to}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {row?.kind !== 'vendor-data-gap' && (
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Target</label>
              <Input type="number" min="1" value={target} onChange={(e) => setTarget(e.target.value)}
                className="h-9" data-testid="edit-target" placeholder="e.g. 20" />
            </div>
          )}
          {row?.kind === 'vendor-data-gap' && (
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Vendors in scope</label>
              <Input value={row?.target || 0} className="h-9 bg-muted/40" disabled />
              <p className="text-[10px] text-muted-foreground mt-1">Fixed by the IDs in the task. Not editable.</p>
            </div>
          )}
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Target time/date</label>
            <div className="grid grid-cols-2 gap-2">
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                className="h-9" data-testid="edit-due-date" />
              <Input type="time" value={dueTime} onChange={(e) => setDueTime(e.target.value)}
                className="h-9" data-testid="edit-due-time" disabled={!dueDate} />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Notes</label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              className="text-xs" data-testid="edit-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="edit-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// --- Share-on-WhatsApp sheet -------------------------------------------------
const ShareAssignmentSheet = ({ open, row, people, onClose }) => {
  if (!row) return null;
  const me = people.find(p => (p.email || '').toLowerCase() === (row.assigned_to_email || row.assigned_to || '').toLowerCase());
  const due = row.due_at ? `\nDue: ${new Date(row.due_at).toLocaleString()}` : '';
  const tgt = row.target ? `\nTarget: ${row.target} listings` : '';
  const notes = row.notes ? `\nNotes: ${row.notes}` : '';
  // Deep-link MUST point at the actual fill surface:
  //   • data-gap task → /pollen-dots-dashboard?gap=<field>&task=<id>  (auto-opens the
  //     inline editor pre-filtered to the assigned vendor list)
  //   • populator task → /pollen-dots/vendors?type=<entity>&task=<id> (lands the
  //     assignee directly on the entity-filtered vendors page with the task
  //     scope banner + Add button highlighted)
  const taskUrl = row.kind === 'vendor-data-gap'
    ? `${window.location.origin}/pollen-dots-dashboard?gap=${row.gap_field || ''}&task=${encodeURIComponent(row.id || '')}`
    : `${window.location.origin}/pollen-dots/vendors?type=${encodeURIComponent(row.entity_type || '')}&task=${encodeURIComponent(row.id || '')}`;
  const msg = `Hi ${me?.name || ''}, you've been assigned a *${row.entity_type}* ${row.kind === 'vendor-data-gap' ? 'data-gap' : 'populator'} task on Pollen Dots.${tgt}${due}${notes}\n\nOpen your task list: ${taskUrl}`;
  const phoneDigits = (me?.phone || '').replace(/\D+/g, '');
  const waUrl = phoneDigits
    ? `https://wa.me/${phoneDigits}?text=${encodeURIComponent(msg)}`
    : `https://wa.me/?text=${encodeURIComponent(msg)}`;
  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="bottom" className="max-h-[60vh]" data-testid="share-task-sheet">
        <SheetHeader>
          <SheetTitle className="text-base">Share on WhatsApp</SheetTitle>
          <SheetDescription className="text-[11px]">
            Sends the task summary + deep-link to {me?.name || row.assigned_to}.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-3 space-y-3">
          <div className="rounded-lg border bg-emerald-50 border-emerald-200 p-3 text-xs whitespace-pre-wrap">
            {msg}
          </div>
          <a href={waUrl} target="_blank" rel="noopener noreferrer"
            className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium"
            data-testid="share-task-whatsapp">
            <Megaphone className="w-4 h-4" />Send on WhatsApp
          </a>
          <button type="button" onClick={() => { navigator.clipboard?.writeText(taskUrl); toast.success('Link copied'); }}
            className="text-[11px] text-muted-foreground underline w-full text-center" data-testid="share-task-copy">
            Copy task link
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Renders a "in N days" / "in N hours" string between two ISO timestamps.
const tatLabel = (startIso, endIso) => {
  try {
    const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
    const h = ms / 36e5;
    if (h < 1) return `${Math.max(1, Math.round(ms / 60000))}m`;
    if (h < 48) return `${Math.round(h)}h`;
    return `${Math.round(h / 24)}d`;
  } catch { return ''; }
};



export default PollenDotsMyProfile;
